import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createRoot } from "react-dom/client";
import { useParams } from "react-router-dom";
import { Tooltip } from "reactstrap";
import PropTypes from "prop-types";
import cx from "classnames";

import global from "../../scss/dhp.scss";
import PageTopOption from "./PageTopOption";
import PageBottomOption from "./PageBottomOption";
import ChartCreate from "../../components/Editor/ChartCreate";
import SnapGrid from "../../components/Editor/Widgets/SnapGrid";
import WidgetHandler from "../../components/Editor/WidgetHandler";
import ActivePageSelector from "../../components/Editor/ActivePageSelector";
import PageGuideLine from "../../components/Editor/PageBlocks/PageGuideLine";
import CollaborativeSelector from "../../components/Editor/CollaborativeSelector";
import { Icon } from "../../components/ui/icon";
import { EditorPanelContext } from "./EditorPanel";
import { EditorWrapperContext } from "./EditorWrapper";

import { AppContext } from "../../contexts";
import { EditorContext } from "./EditorLayout";
import {
  MAP,
  TEXT,
  TABLE,
  STOCK,
  VIDEO,
  BRAND,
  UPLOAD,
  PICTURE,
  COLLAGE,
  JOT_FORM,
  TYPE_FORM,
  TEXT_FRAME,
  WHITE_COLOR,
  BLACK_COLOR,
  GROUP_WIDGET,
  UPLOAD_VIDEO,
  TYPE_BROCHURE,
  TYPE_PROPOSAL,
  TYPE_INFOGRAPHIC,
  TYPE_YOUTUBE_BANNER,
  WIDGET_HANDLER_MARGIN_TOP,
  WIDGET_HANDLER_MARGIN_LEFT,
  GENERIC_EDITOR_WRAP_PADDING,
  PRICING_TABLE,
  FRAME,
  CHART,
  SCOPE_OF_WORK,
  TIMELINE,
  ITINERARY,
  TYPE_INTERACTIVE_DEMO,
  BUTTON,
  PORTFOLIO,
  CASESTUDY,
  COUNTDOWN,
} from "../../constants/editor";
import {
  RGBToHex,
  getZoomedValue,
  toggleZoomStyle,
  getRandomString,
  isOnlyTextInGroup,
  getCssTransformObj,
  getClassListToString,
  getCssTextObjToString,
  resetEditorMapTooltip,
  checkChildOutsideParent,
  getDimentionInCurrentUOM,
  getCssTransformObjToString,
  getUpdatedGroupInnerWidgetPos,
  calculateNewPositionOnRotatedObjectResize,
  changeBucketColorAccordingBackground,
  hexToRGB,
  processMfs,
} from "../../_helpers/utils";

import useCollage from "../../hooks/useCollage";
import usePagePlay from "../../hooks/usePagePlay";
import useAudioPlay from "../../hooks/useAudioPlay";
import useDraggable from "../../hooks/useDraggable";
import useClipboard from "../../hooks/useClipboard";
import useCutWidget from "../../hooks/useCutWidget";
import useSnaptoGrid from "../../hooks/useSnaptoGrid";
import useSelectable from "../../hooks/useSelectable";
import useCopyWidget from "../../hooks/useCopyWidget";
import usePasteWidget from "../../hooks/usePasteWidget";
import useCloneWidget from "../../hooks/useCloneWidget";
import useMergeFields from "../../hooks/useMergeFields";
import useBlockReorder from "../../hooks/useBlockReorder";
// import useDocumentPlay from "../../hooks/useDocumentPlay";
import useSortableList from "../../hooks/useSortableList";
import useTextFocusOut from "../../hooks/useTextFocusOut";
import useDocumentPlayV2 from "../../hooks/useDocumentPlayV2";
import useSetBlockHeight from "../../hooks/useSetBlockHeight";
import useFontFormatting from "../../hooks/useFontFormatting";
import useDocumentReader from "../../hooks/useDocumentReader";
import useCopyStyleWidget from "../../hooks/useCopyStyleWidget";
import useWidgetHighlighter from "../../hooks/useWidgetHighlighter";
import useSetActivePageBlock from "../../hooks/useSetActivePageBlock";
import useBlockActiveOnscroll from "../../hooks/useBlockActiveOnscroll";
import useHandelKeyBoardEvent from "../../hooks/useHandelKeyBoardEvent";
import useReplaceDuplicateShapeId from "../../hooks/useReplaceDuplicateShapeId";
import useUploadInsertOutsideImage from "../../hooks/useUploadInsertOutsideImage";
import useUploadInsertOutsideVideo from "../../hooks/useUploadInsertOutsideVideo";
import useEditorDocumentProcessing from "../../hooks/useEditorDocumentProcessing";
import useAddWidgetOnDocumentCreate from "../../hooks/useAddWidgetOnDocumentCreate";
import usePricingTableEvent from "../../hooks/usePricingTableEvent";

import { fetchDocumentDetails } from "../../store/actions/documentActions";
import useScopeOfWorkEvent from "../../hooks/useScopeOfWorkEvent";
import useItineraryEvent from "../../hooks/useItineraryEvent";
import { SPINNER_FIELDS_META_INFOGRAPHIC } from "../../constants/pageSizeModal";
import useCountdown from "../../hooks/useCountdown";
import { widgetConfig } from "../../components/Editor/editor_config";

let style = Object.assign({}, global);

const rootMap = new Map();

const BlockBackgroundColor = props => {
  return <div className="dhp-page-bgcolor-overlay" style={props.backgroundColor.style}></div>;
};
//Props type validation
BlockBackgroundColor.propTypes = {
  backgroundColor: PropTypes.object,
};

const BlockBackgroundImage = props => {
  return <div className="dhp-page-overlay" style={props.backgroundImage.style}></div>;
};
//Props type validation
BlockBackgroundImage.propTypes = {
  backgroundImage: PropTypes.object.isRequired,
};

const Widget = props => {
  let {
    rightContextMenu,
    updateRightContextMenu,
    metadata,
    dimension,
    snapGrid,
    documentType,
    isTimeLineViewOpen,
    isDocumentSigned,
    setClonedObjRef,
    isFrameContentActive,
  } = useContext(EditorContext);
  let { updateDoubleClickActive, copyStyle, updateTogglePlayButton } = useContext(EditorPanelContext);
  let curWidget = JSON.parse(JSON.stringify(props.widget));

  const widgetClassList = getClassListToString(curWidget.classLists.filter(cls => cls !== "child-selected"));
  const widgetInnerHtml = curWidget.innerHTML.replace(/(\s*)child-selected(\s*)/g, "");
  const safariBugfixCss = props.isSafari ? { willChange: "filter" } : {};
  const iframePageSorterBugfixCss = [TYPE_FORM, JOT_FORM].includes(curWidget.data["data-asset-type"])
    ? { pointerEvents: "none" }
    : {};

  const widgetRef = useRef(null);

  const [activeWidgetInfo, setAactiveWidgetInfo] = useState();
  const [sorterInnerHtml, setSorterInnerHtml] = useState();

  const { start: initSelectable } = useSelectable();
  const { start: initDraggable } = useDraggable();
  const { show: show_WHT, hide: hide_WHT } = useWidgetHighlighter();
  const setCopyStyleWidget = useCopyStyleWidget();
  const { replaceDuplicateIdFromSVG, repalceDuplicateIdFromFrame } = useReplaceDuplicateShapeId();
  useSetActivePageBlock(activeWidgetInfo); // For set active page block widget by Hook
  useSnaptoGrid();
  const { fetchCollageEvent, activateCollageEvent, collageItemDrag: initCollageItemDrag } = useCollage();

  // show right context menu on right click on Widget
  const showRightContextMenu = e => {
    e.preventDefault();

    if (widgetRef.current.dataset.layerLocked === "true" || localStorage.getItem("widget.event.isActive") === "true")
      return;

    let isWidgetGroupSelected = e.target.closest(".group-selected");

    // do not set any active widget is multiple widget is selected
    if (!isWidgetGroupSelected) {
      const isGroupWidget = e.target.closest(".dhp-page-group");
      const isClickedOnChildOfAGroupWidget = isGroupWidget && e.target.closest(".dhp-page-widget");
      setActiveWidget(isClickedOnChildOfAGroupWidget);
    }

    updateRightContextMenu({
      ...rightContextMenu,
      enable: true,
      top: e.clientY - document.querySelector(".editor-wrapper").getBoundingClientRect().top,
      left: e.clientX - document.querySelector(".editor-wrapper").getBoundingClientRect().left,
      style: { ...rightContextMenu.style, top: "0px", left: "0px", visibility: "hidden" },
    });
  };

  const setActiveWidget = isClickedOnChildOfAGroupWidget => {
    if (
      copyStyle.enable &&
      !isClickedOnChildOfAGroupWidget &&
      document.getElementById(widgetRef.current.id).dataset.assetType !== GROUP_WIDGET
    )
      setCopyStyleWidget(widgetRef); // prevent apply copy style in a group widget or any widget under the group

    setAactiveWidgetInfo({
      targetPageIdx: document.getElementById(widgetRef.current.id).closest(".dhp-page-canvas").dataset.pageIdx,
      targetBlockIdx: document.getElementById(widgetRef.current.id).closest(".dhp-page-block").dataset.blockIdx,
      activeWidgetId: [isClickedOnChildOfAGroupWidget?.id ?? widgetRef.current.id],
      activeWidgetType: [
        isClickedOnChildOfAGroupWidget?.dataset.assetType ??
          document.getElementById(widgetRef.current.id).dataset.assetType,
      ],
      disableAutoScroll: !props.pageSorterList ? true : false,
    });
  };

  const handleDoubleClick = e => {
    e.preventDefault();
    let isWidgetGroupSelected = e.target.closest(".group-selected");
    let doubleClickActive =
      widgetRef.current.dataset.assetType === GROUP_WIDGET
        ? isOnlyTextInGroup(metadata.activeWidgetType[0], metadata.activeWidgetId[0])
        : true;

    // return if widget is locked or multiple widget is selcted or the selcted text or text frame is in a group widget which contain multiple type asste type or selected text frame is new text frame ie: version 3
    if (
      widgetRef.current.dataset.layerLocked === "true" ||
      isWidgetGroupSelected ||
      !doubleClickActive ||
      (metadata.activeWidgetType[0] === TEXT_FRAME &&
        parseInt(document.getElementById(metadata.activeWidgetId[0]).getAttribute("data-version")) >= 3)
    )
      return;

    // handle Double click on text or text-frame widget
    if (
      [TEXT, TEXT_FRAME, BUTTON].includes(widgetRef.current.dataset.assetType) ||
      [TEXT, TEXT_FRAME, BUTTON].includes(metadata.activeWidgetType[0])
    ) {
      const { top: editorOuterWrapTop } = document.querySelectorAll(".dhp-page-canvas")[0].getBoundingClientRect();
      const { top: activeBlockTop } = document.getElementById(metadata.activeBlockId).getBoundingClientRect();
      const {
        translate: { x: transX, y: transY },
      } = getCssTransformObj({
        transform: widgetRef.current.style.transform,
      });

      const widgetTransformObj = getCssTransformObj({
        translateX: `${getZoomedValue(transX, dimension.zoom)}px`,
        translateY: `${getZoomedValue(transY, dimension.zoom)}px`,
        transform: widgetRef.current.style.transform,
      });

      const widgetTransformStr = getCssTransformObjToString({ transformObj: widgetTransformObj });
      let addedLeft = getZoomedValue(WIDGET_HANDLER_MARGIN_LEFT + GENERIC_EDITOR_WRAP_PADDING, dimension.zoom);
      let addedTop = getZoomedValue(WIDGET_HANDLER_MARGIN_TOP + GENERIC_EDITOR_WRAP_PADDING, dimension.zoom);
      let newClassLists = ["dhp-content-editable-true-text", "draggable-disabled"];

      // Set new style property for outside text/text-frame widget
      let styleProperty = {
        ...widgetRef.current.style,
        position: "absolute",
        // opacity: widgetRef.current.style.opacity,
        width: `${getZoomedValue(widgetRef.current.style.width, dimension.zoom)}px`,
        height: `${getZoomedValue(widgetRef.current.style.height, dimension.zoom)}px`,
        transform: widgetTransformStr,
        left: [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
          ? `${addedLeft}px`
          : isTimeLineViewOpen
            ? "0px"
            : `${WIDGET_HANDLER_MARGIN_LEFT + GENERIC_EDITOR_WRAP_PADDING}px`,
        top: [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
          ? `${Math.abs(activeBlockTop - editorOuterWrapTop) + addedTop}px`
          : isTimeLineViewOpen
            ? "0px"
            : `${
                Math.abs(activeBlockTop - editorOuterWrapTop) +
                (WIDGET_HANDLER_MARGIN_TOP + GENERIC_EDITOR_WRAP_PADDING)
              }px`,
      };

      // remove previous active class and set active class on current target in text frame
      if (widgetRef.current.dataset.assetType === TEXT_FRAME || metadata.activeWidgetType[0] === TEXT_FRAME) {
        document.querySelectorAll(`#${widgetRef.current.id} .active-text`).forEach(function (node) {
          node.classList.remove("active-text");
        });

        if (e.target.nodeName === "DIV") {
          if (e.target.querySelector(".title")) e.target.querySelector(".title").classList.add("active-text");
          else e.target.closest(".title").classList.add("active-text");
        } else e.target.closest("svg").querySelector(".textwrap div .title").classList.add("active-text");
      }

      // if font weight, style and decoration is applied previously in whole widget update the structure to child divs also like new structure(text version 3)
      if (widgetRef.current.dataset.assetType === TEXT && widgetRef.current.dataset.version < 3.0) {
        let target = widgetRef.current;
        let targetinner = target.querySelector(".primary-text-widget");

        if (!targetinner.firstChild.tagName) {
          let updatedInnerHtml = document.createElement("div");
          updatedInnerHtml.innerHTML = targetinner.innerHTML;
          targetinner.innerHTML = updatedInnerHtml.outerHTML;
        }

        if (
          parseInt(targetinner.style.fontWeight) === 700 ||
          targetinner.style.fontStyle === "italic" ||
          (targetinner.style.textDecoration && targetinner.style.textDecoration === "underline")
        ) {
          targetinner.querySelectorAll("*").forEach(element => {
            if (parseInt(targetinner.style.fontWeight) === 700) element.style.fontWeight = 700;
            if (targetinner.style.fontStyle === "italic") element.style.fontStyle = "italic";
            if (targetinner.style.textDecoration && targetinner.style.textDecoration === "underline")
              element.style.textDecoration = "underline";
          });
        }
      }

      // remove last extra div from node, if there is no content inside
      if (widgetRef.current.dataset.assetType === TEXT) {
        let target = widgetRef.current;
        let targetinner = target.querySelector(".primary-text-widget");

        // Get all child divs
        const divs = targetinner.querySelectorAll("div");

        // Check the last div
        if (divs.length > 0) {
          const lastDiv = divs[divs.length - 1];

          // Remove it if it's empty or only contains whitespace
          if (!lastDiv.textContent.trim() && lastDiv.children.length === 0) {
            lastDiv.remove();
          }
        }
      }

      // remove hyperlink class if widget has hyperlinked
      widgetRef.current.classList.forEach(className => {
        if (className !== "dhp-widget-hyperlinked") newClassLists.unshift(className);
      });

      props.setOutsideDoubleclickedObj({
        classLists: newClassLists,
        style: styleProperty,
        dataWidgetId: widgetRef.current.id,
        dataAssetType: widgetRef.current.dataset.assetType,
        innerHTML: widgetRef.current.innerHTML,
      });

      updateDoubleClickActive(true);
    }

    // handle Double click on collage widget
    if (COLLAGE === widgetRef.current.dataset.assetType) {
      activateCollageEvent({ e, widgetNode: widgetRef.current });
    }
  };

  const checkClick = e => {
    if (
      widgetRef.current.dataset.assetType === TABLE ||
      widgetRef.current.dataset.assetType === MAP ||
      widgetRef.current.dataset.assetType === PRICING_TABLE ||
      widgetRef.current.dataset.assetType === SCOPE_OF_WORK ||
      widgetRef.current.dataset.assetType === TIMELINE ||
      widgetRef.current.dataset.assetType === ITINERARY ||
      widgetRef.current.dataset.assetType === PORTFOLIO ||
      widgetRef.current.dataset.assetType === CASESTUDY
    )
      return;

    // more than double click edit text ot text frame
    if (e.detail >= 2) {
      handleDoubleClick(e);
    }
  };

  const hoverIn = e => {
    if (e.target.closest(".dhp-root-widget").getAttribute("data-asset-type") === FRAME && isFrameContentActive) return;
    if (copyStyle.enable) e.target.closest(".dhp-root-widget").classList.add("cursor-copy-style");
    show_WHT({ hoverWidgetId: e.target.closest(".dhp-root-widget").id });
  };

  const hoverOut = e => {
    if (e.target.closest(".dhp-root-widget").getAttribute("data-asset-type") === FRAME && isFrameContentActive) return;
    e.target.closest(".dhp-root-widget").classList.remove("cursor-copy-style");
    hide_WHT();
    resetEditorMapTooltip();
  };

  const mouseDownSubscribers = e => {
    if (isDocumentSigned) return;

    // scroll to active widget bar when active any widget from canvas
    if (isTimeLineViewOpen === "page-timeline") {
      document.querySelector(".timeline-body").scrollTop = 0;
      let parentPos = document.querySelector(".timeline-body").getBoundingClientRect().top;
      let element = document.querySelector(`#page-timeline-${curWidget.id}`);
      let childPos = element?.getBoundingClientRect().top;
      let targetOffset = childPos - parentPos;

      document.querySelector(".timeline-body").scrollTop = targetOffset - 30;
    }

    hide_WHT();
    const isLeftClick = e.button === 0;
    const isShiftKeyPressed = e.shiftKey;
    const isWidgetGroupSelected = e.target.closest(".group-selected");
    const { isActive: isCollageEventActive, widgetId: collageWidgetId } = fetchCollageEvent();
    const isClickedOnActiveCollageItem =
      isCollageEventActive && e.target.closest(`#${collageWidgetId} .collage-item.active`);
    const isClickedOnActiveFrameItem = isFrameContentActive && e.target.closest("#highlightImage");

    if (isClickedOnActiveFrameItem) return;

    // NOTE:::: Only when left mouse button is pressed i.e. MouseEvent.button = 0 AND shift key is not pressed when clicked
    if (isLeftClick && !isShiftKeyPressed) {
      if (isWidgetGroupSelected) {
        initDraggable({ widgetId: curWidget.id }, e);
      } else if (isClickedOnActiveCollageItem) {
        initCollageItemDrag({ widgetId: collageWidgetId, domEvent: e });
      } else {
        const isGroupWidget = e.target.closest(".dhp-page-group");
        const isClickedOnChildOfAGroupWidget = isGroupWidget && e.target.closest(".dhp-page-widget");
        const activeWId = isClickedOnChildOfAGroupWidget?.id ?? curWidget.id;

        // set previous cloned obj ref false if active widget change
        if (activeWId !== metadata.activeWidgetId[0]) {
          setClonedObjRef(false);
        }

        setActiveWidget(isClickedOnChildOfAGroupWidget);
        initSelectable({ activeWidgetId: [activeWId], finalUpdate: true });
        if (!copyStyle.enable) initDraggable({ widgetId: curWidget.id }, e);

        // pause video on change active widget and select
        if (
          metadata.activeWidgetId[0] !== activeWId &&
          [STOCK, BRAND, UPLOAD_VIDEO].includes(document.getElementById(activeWId).getAttribute("data-source"))
        ) {
          updateTogglePlayButton(false);
        }
      }
    }
  };

  const handleMouseMove = e => {
    // for map hover
    if (
      e.target.closest(".dhp-page-widget")?.getAttribute("data-asset-type") === MAP &&
      e.target.closest("svg")?.getAttribute("data-caption-on") === "true" &&
      e.target.tagName === "path"
    ) {
      let title = e.target.getAttribute("title");
      let dataCaptionType = e.target.closest("svg").getAttribute("data-caption-type");
      let background = dataCaptionType === "Light" ? WHITE_COLOR : BLACK_COLOR;
      let color = dataCaptionType === "Light" ? BLACK_COLOR : WHITE_COLOR;
      const x = e.clientX;
      const y = e.clientY;
      let tableCaptionObj;
      let targetPath = e.target.closest("path");
      const dataVersion = parseInt(e.target.closest(".dhp-page-widget")?.getAttribute("data-version"));

      if (targetPath) {
        const attributes = targetPath.attributes;
        for (let i = 0; i < attributes.length; i++) {
          const attributeName = attributes[i].name;
          let attributeValue = attributes[i].value;

          // version-specific logic
          if (dataVersion >= 4 && attributeName.startsWith("data-")) {
            attributeValue = JSON.parse(attributeValue);
            tableCaptionObj = {
              ...tableCaptionObj,
              [`${attributeValue.name}`]: attributeValue.value ? attributeValue.value : 0,
            };
          } else if (attributeName.startsWith("data-")) {
            let formattedKey = attributeName.replace(/^data-/, ""); // Removed "data-"
            // Capitalize first character
            formattedKey = formattedKey.charAt(0).toUpperCase() + formattedKey.slice(1);
            tableCaptionObj = { ...tableCaptionObj, [formattedKey]: attributeValue ? attributeValue : 0 };
          }
        }
      }

      const tooltipDOM = document.getElementById("editor-map-tooltip");
      const areaTitle = document.getElementById("areaTitle");
      const captionDOM = document.getElementById("caption");

      tooltipDOM.style.cssText = getCssTextObjToString({
        left: `${x + 10}px`,
        top: `${y + 10}px`,
        background: background,
        color: color,
        position: "fixed",
        padding: "5px",
        "border-radius": "5px",
        display: "inline-block",
      });
      areaTitle.innerText = title;
      captionDOM.innerHTML = "";

      Object.entries(tableCaptionObj).forEach(([key, value]) => {
        // Replacing underscores with spaces
        let formattedKey = key.replace(/_/g, " ");
        let li = document.createElement("li");
        li.innerText = `${formattedKey}: ${value}`;
        captionDOM.appendChild(li);
      });
    }
    // for itinerary hover
    else if (
      e.target.closest(".dhp-page-widget")?.getAttribute("data-asset-type") === ITINERARY &&
      e.target.closest(".website-link")
    ) {
      const x = e.clientX;
      const y = e.clientY;
      const tooltipDOM = document.getElementById("editor-map-tooltip");
      const areaTitle = document.getElementById("areaTitle");
      const captionDOM = document.getElementById("caption");

      tooltipDOM.style.cssText = getCssTextObjToString({
        left: `${x + 10}px`,
        top: `${y + 10}px`,
        background: "#000000",
        color: "#ffffff",
        position: "fixed",
        padding: "5px",
        "border-radius": "5px",
        display: "inline-block",
      });
      areaTitle.innerText = e.target.getAttribute("data-url");
      captionDOM.innerHTML = "";
    } else {
      resetEditorMapTooltip();
    }
  };

  useLayoutEffect(() => {
    if (props.pageSorterList) {
      let targetDOM = document.createElement("div");
      targetDOM.innerHTML = curWidget.innerHTML;

      targetDOM.querySelectorAll(".dhp-page-widget").forEach(element => {
        element.removeAttribute("id");
        element.classList.remove("child-selected");
      });

      targetDOM.querySelectorAll(".dhp-widget-inner").forEach(element => {
        let widgetDataSet = curWidget.data;
        if (
          [TEXT, BUTTON].includes(widgetDataSet["data-asset-type"]) &&
          element.style.backgroundColor !== "transparent"
        )
          element.classList.add("text-background-active");

        if ([PRICING_TABLE].includes(widgetDataSet["data-asset-type"])) {
          element.querySelectorAll('input[type="radio"]').forEach(el => {
            el.name = el.name + "-preview";
          });
        }
      });

      let widgetDataSet = props.widget.data;
      let config = props.widget.chartConfig;

      let animationFrameId = null;

      const renderChart = () => {
        if ([CHART].includes(widgetDataSet["data-asset-type"])) {
          const domNode = document.querySelector(`[data-id="${props.widget.id}"] .dhp-widget-inner`);
          if (domNode) {
            let root = rootMap.get(domNode);
            if (!root) {
              root = createRoot(domNode);
              rootMap.set(domNode, root);
            }
            root.render(
              <ChartCreate key={props.widget.id} chartData={config.chartBasedConfig} labelFormat={config.labelFormat} />
            );
          }
        }
      };

      animationFrameId = requestAnimationFrame(renderChart);

      replaceDuplicateIdFromSVG(); // Replace duplicate id for shape use in shape border
      repalceDuplicateIdFromFrame(); // Replace duplicate id for frame

      setSorterInnerHtml(targetDOM.innerHTML);

      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    }
  }, [props.pageSorterList, props.widget]);

  useLayoutEffect(() => {
    let widgetDataSet = props.widget.data;
    let config = props.widget.chartConfig;

    let pixelRatio = 4;
    if (dimension.zoom < 100) {
      pixelRatio = Math.max((dimension.zoom / 100) * 5, 1);
    }

    if ([CHART].includes(widgetDataSet["data-asset-type"])) {
      const domNode = document.querySelector(`#${props.widget.id} .dhp-widget-inner`);
      if (domNode) {
        let root = rootMap.get(domNode);
        if (!root) {
          root = createRoot(domNode);
          rootMap.set(domNode, root);
        }
        root.render(
          <ChartCreate
            key={props.widget.id}
            chartData={config.chartBasedConfig}
            labelFormat={config.labelFormat}
            pixelRatio={Math.floor(pixelRatio)}
          />
        );
      }
    }
  }, [props.widget, dimension.zoom]);

  useEffect(() => {
    let targetDOM = document.createElement("div");
    targetDOM.innerHTML = curWidget.innerHTML;
    targetDOM.querySelectorAll(".dhp-widget-inner").forEach(element => {
      let widgetDataSet = curWidget.data;
      if ([PICTURE, UPLOAD, VIDEO].includes(widgetDataSet["data-asset-type"]) && props.isSafari)
        element.style.willChange = "transform";

      if ([TEXT, BUTTON].includes(widgetDataSet["data-asset-type"]) && element.style.backgroundColor !== "transparent")
        element.classList.add("text-background-active");
    });
    curWidget.innerHTML = targetDOM.innerHTML;
  }, []);

  return (
    <>
      {props.pageSorterList && (
        <>
          {curWidget.data["data-asset-type"] !== COUNTDOWN && (
            <div
              style={{ ...curWidget.style, ...safariBugfixCss, ...iframePageSorterBugfixCss }}
              {...curWidget.data}
              {...(curWidget.classLists.includes("dhp-widget-hyperlinked") && {
                className: "nav-widget dhp-widget-hyperlinked",
              })}
              {...(!curWidget.classLists.includes("dhp-widget-hyperlinked") && { className: "nav-widget" })}
              dangerouslySetInnerHTML={{
                __html:
                  props.widget.data["data-asset-type"] === CHART && props.renderChart === false
                    ? `<img style="width: 100%" src=${props.widget.chartThumb} />`
                    : sorterInnerHtml,
              }}
              data-id={curWidget.id}></div>
          )}

          {curWidget.data["data-asset-type"] === COUNTDOWN && (
            <div
              style={{ ...curWidget.style, ...safariBugfixCss, ...iframePageSorterBugfixCss }}
              {...curWidget.data}
              {...(curWidget.classLists.includes("dhp-widget-hyperlinked") && {
                className: "nav-widget dhp-widget-hyperlinked",
              })}
              {...(!curWidget.classLists.includes("dhp-widget-hyperlinked") && { className: "nav-widget" })}
              data-id={curWidget.id}>
              <CountdownRender config={curWidget.config} />
            </div>
          )}
        </>
      )}
      {!props.pageSorterList && (
        <>
          {/* widgets Grid Lines for snap to grid */}
          {snapGrid?.widgetGrid?.map(
            (grid, idx) => grid.widgetId == curWidget.id && <SnapGrid key={idx} grid={grid} component={"widget"} />
          )}

          {curWidget.data["data-asset-type"] === COUNTDOWN && (
            <div
              ref={widgetRef}
              className={widgetClassList}
              id={curWidget.id}
              style={{ ...curWidget.style, ...safariBugfixCss }}
              onClick={e => checkClick(e)}
              onDoubleClick={e => handleDoubleClick(e)}
              onMouseDown={e => mouseDownSubscribers(e)}
              onMouseEnter={e => hoverIn(e)}
              onMouseLeave={e => hoverOut(e)}
              onContextMenu={e => showRightContextMenu(e)}
              onDragStart={e => e.preventDefault()}
              onMouseMove={e => handleMouseMove(e)}
              {...curWidget.data}>
              <CountdownRender config={curWidget.config} />
            </div>
          )}

          {curWidget.data["data-asset-type"] !== COUNTDOWN && (
            <div
              ref={widgetRef}
              className={widgetClassList}
              id={curWidget.id}
              style={{ ...curWidget.style, ...safariBugfixCss }}
              onClick={e => checkClick(e)}
              onDoubleClick={e => handleDoubleClick(e)}
              onMouseDown={e => mouseDownSubscribers(e)}
              onMouseEnter={e => hoverIn(e)}
              onMouseLeave={e => hoverOut(e)}
              onContextMenu={e => showRightContextMenu(e)}
              onDragStart={e => e.preventDefault()}
              onMouseMove={e => handleMouseMove(e)}
              {...curWidget.data}
              dangerouslySetInnerHTML={{ __html: processMfs(widgetInnerHtml, curWidget) }}></div>
          )}
        </>
      )}
    </>
  );
};

//Props type validation
Widget.propTypes = {
  widget: PropTypes.object,
  pageSorterList: PropTypes.bool,
  scaleFactor: PropTypes.number,
  setOutsideDoubleclickedObj: PropTypes.func,
  isSafari: PropTypes.bool,
};

const CountdownRender = ({ config }) => {
  const innerRef = useRef(null);
  const { d, h, m, s } = useCountdown(config);
  const scale = config.scale || 1;
  const textColor =
    changeBucketColorAccordingBackground(hexToRGB(config.backgroundColor)) === "dark" ? "#ffffff" : "#000000";
  const blocks =
    d > 0
      ? [
          { label: "DAYS", value: d },
          { label: "HOURS", value: h },
          { label: "MINUTES", value: m },
        ]
      : [
          { label: "HOURS", value: h },
          { label: "MINUTES", value: m },
          { label: "SECONDS", value: s },
        ];

  useEffect(() => {
    if (!innerRef.current) return;

    const style = config.countdownStyle || {};

    // FORCE APPLY
    if (style.opacity !== undefined) {
      innerRef.current.style.opacity = style.opacity;
    }

    if (style.filter) {
      innerRef.current.style.filter = style.filter;
    }
  }, [config.countdownStyle]);

  return (
    <div
      key={JSON.stringify(config.countdownStyle)}
      ref={innerRef}
      className="dhp-widget-inner"
      style={{
        ...widgetConfig[COUNTDOWN].innerHTML.style,

        // APPLY USER STYLES
        ...(config.countdownStyle || {}),

        // fallback (used before resize)
        "--countdown-font": `${24 * scale}px`,
        "--countdown-label": `${10 * scale}px`,
        "--countdown-gap": `${6 * scale}px`,
      }}>
      {/* ================= STYLE 1 ================= */}
      {config.style === "style 1" && (
        <div
          style={{
            display: "flex",
            gap: "var(--countdown-gap)",
            width: "100%",
            height: "100%",
          }}>
          {blocks.map((b, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: config.backgroundColor,
                color: textColor,
                borderRadius: 6,
                fontFamily: config.fontFamily,
              }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "var(--countdown-font)",
                }}>
                {String(b.value).padStart(2, "0")}
              </div>

              <div
                style={{
                  fontSize: "var(--countdown-label)",
                }}>
                {b.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================= STYLE 2 ================= */}
      {config.style === "style 2" && (
        <div
          style={{
            display: "flex",
            gap: "var(--countdown-gap)",
            width: "100%",
            height: "100%",
          }}>
          {blocks.map((b, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: config.backgroundColor,
                color: textColor,
                borderRadius: 6,
                fontFamily: config.fontFamily,
              }}>
              <div style={{ fontSize: "var(--countdown-label)" }}>{b.label}</div>

              <div
                style={{
                  width: "60%",
                  height: 1,
                  background: "currentColor",
                  opacity: 0.4,
                  margin: "4px 0",
                }}
              />

              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "var(--countdown-font)",
                }}>
                {String(b.value).padStart(2, "0")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Block = props => {
  let {
    documentType,
    backgroundColors,
    backgroundImages,
    widgets,
    rightContextMenu,
    updateRightContextMenu,
    dimension,
    uploadState,
    setUploadState,
    setClonedObjRef,
    disableEditor,
  } = useContext(EditorContext);
  const { updateScrollEvent } = useContext(EditorWrapperContext);

  const blockContainer = useRef(null);
  const refBottomResize = useRef(null);
  useAddWidgetOnDocumentCreate({ blockNode: blockContainer.current });
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  // const safariBugfixCss = isSafari ? { overflow: "", overflowY: "hidden" } : {};
  const safariBugfixCss = {};

  // local states
  const [activeContainerInfo, setActiveContainerInfo] = useState();
  const [blockHeight, setBlockHeight] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipValue, setTooltipValue] = useState();
  const [isResize, setIsResize] = useState();
  const [isClipBoardExist, setIsClipBoardExist] = useState(false);

  // global states
  const companyInfo = useSelector(state => state?.auth?.company);

  // used custom hooks
  useSetActivePageBlock(activeContainerInfo); // For set active page block by Hook
  useSetBlockHeight(isResize, blockContainer, blockHeight); // Set block height on resize block
  const { read: readFromClipboard } = useClipboard();
  const { uploadOutsideImage } = useUploadInsertOutsideImage();
  const { uploadOutsideVideo } = useUploadInsertOutsideVideo();

  // Set Active container info to update hook -> useSetActivePageBlock
  const setActivePage = e => {
    let elemnt = e.current ?? e.target;
    if (
      elemnt.classList.contains("dhp-page-block") ||
      elemnt.classList.contains("nav-block") ||
      elemnt.closest(".nav-block") ||
      elemnt.classList.contains("dhp-page-overlay") ||
      elemnt.classList.contains("dhp-page-bgcolor-overlay")
    ) {
      // Note : keep this commented code
      // if (activeBlockId === blockContainer.current.id && !activeOutSideCanvasArea && !activeWidgetId) return;
      setClonedObjRef(false);

      // To prevent auto scroll
      updateScrollEvent(true);

      setActiveContainerInfo({
        // targetPageIdx: blockContainer.current.parentNode.dataset.pageIdx,
        targetPageIdx:
          blockContainer.current.closest(".dhp-page-canvas")?.dataset?.pageIdx ??
          blockContainer.current.parentNode.dataset.pageIdx,
        targetBlockIdx: blockContainer.current.dataset.blockIdx,
        disableAutoScroll: !props.pageSorterList ? true : false,
      });
    }

    //Page sorter add class to show page list controller, on active page click, add time out to stop transition when controller move to active page
    setTimeout(() => {
      props.pageSorterList &&
        document.querySelector(".page-sorter-controller").classList.add("show-page-list-controller");
    }, 300);
  };

  // check clipboard data is available or not
  const checkClipBoardData = async () => {
    const { dataSource, dataType, data, isValidWidget } = await readFromClipboard();

    setIsClipBoardExist(
      isValidWidget &&
        ((dataSource === "dhp" && data?.companyId === companyInfo?._id) ||
          (dataSource === "non_dhp" && dataType === "image/png"))
    );
  };

  // show right context menu on right click on block
  const showRightContextMenu = e => {
    checkClipBoardData();
    let rightClickOnBlock = false;
    e.preventDefault();
    setActivePage(e);

    if (
      e.target.classList.contains("dhp-page-block") ||
      e.target.classList.contains("nav-block") ||
      e.target.closest(".nav-block") ||
      e.target.classList.contains("dhp-page-overlay") ||
      e.target.classList.contains("dhp-page-bgcolor-overlay")
    )
      rightClickOnBlock = true;

    if (props.pageSorterList || !isClipBoardExist || !rightClickOnBlock) return;

    updateRightContextMenu({
      ...rightContextMenu,
      enable: true,
      top: e.clientY - document.querySelector(".editor-wrapper").getBoundingClientRect().top,
      left: e.clientX - document.querySelector(".editor-wrapper").getBoundingClientRect().left,
      style: { ...rightContextMenu.style, top: "0px", left: "0px", visibility: "hidden" },
    });
  };

  const overrideDefault = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const fileHover = () => document.getElementById(props.blockNode.blockId).classList.add("fileHover");

  const fileHoverEnd = () => document.getElementById(props.blockNode.blockId).classList.remove("fileHover");

  // Drag and drop outside file to upload in canvas
  const addFiles = e => {
    let droppedFiles = e.target.files || e.dataTransfer.files;

    let allowedImageTypes = ["jpeg", "jpg", "png", "svg+xml", "gif"];
    let allowedVideoTypes = ["mp4", "mov", "webm", "quicktime"];
    const maxImazeSizeInBytes = 5 * 1024 * 1024;
    const maxVideoSizeInBytes = 100 * 1024 * 1024;

    if (droppedFiles[0]) {
      const file = droppedFiles[0];
      const fileType = file.type.split("/").pop(); // Get file extension

      // Check if the file type is allowed and not exceeded limit
      if (allowedImageTypes.includes(fileType.toLowerCase()) && file.size <= maxImazeSizeInBytes) {
        uploadOutsideImage(file);
        setUploadState({ ...uploadState, isOutSideAssetUpload: true });
      } else if (allowedVideoTypes.includes(fileType.toLowerCase()) && file.size <= maxVideoSizeInBytes) {
        uploadOutsideVideo(file);
        setUploadState({ ...uploadState, isOutSideAssetUpload: true });
      } else {
        return; // Exit function if not allowed
      }
    }
  };

  useEffect(() => {
    if ([TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)) {
      const resizableElem = blockContainer.current;
      let height = 0;
      let y = 0;

      const onMouseMoveBottomResize = event => {
        event.preventDefault();
        props.setIsResizing(true);
        if (!tooltipOpen) setTooltipOpen(true); // Enable tooltip on drag
        let ActivePageHeight = document.getElementById("active-page-selctor").style.height;
        const dy = event.clientY - y;

        if (
          height + dy > 79 &&
          (height + dy <= SPINNER_FIELDS_META_INFOGRAPHIC.px.maxHeight ||
            height + dy < parseFloat(ActivePageHeight) ||
            parseFloat(ActivePageHeight) < SPINNER_FIELDS_META_INFOGRAPHIC.px.maxHeight)
        ) {
          height = height + dy;

          if (height > SPINNER_FIELDS_META_INFOGRAPHIC.px.maxHeight) {
            height = SPINNER_FIELDS_META_INFOGRAPHIC.px.maxHeight;
          }
          y = event.clientY;
          setTooltipValue(
            `${getDimentionInCurrentUOM(height, dimension?.displayUnit)} ${dimension?.displayUnit ?? dimension?.unit}`
          );
          resizableElem.style.height = `${height}px`;
          //setting editor-outer-wrap height on resize
          let blockNodes = document.querySelectorAll(".dhp-page-block");
          let totalPageHeight = 0;
          blockNodes?.forEach(blockInfo => {
            totalPageHeight += parseFloat(blockInfo.style.height);
          });

          document.querySelector(".editor-outer-wrap").style.height = `${
            getZoomedValue(parseInt(totalPageHeight), dimension.zoom) + 80
          }px`;
          let pageGrid = document.querySelector(".dhp-page-grid");
          if (pageGrid) {
            pageGrid.style.height = `${totalPageHeight}px`;
            pageGrid.getElementsByTagName("svg")[0].setAttribute("height", `${totalPageHeight}px`);
          }

          // increase or decrease generic-editor-wrap height
          let curEditorWrap = document.getElementById("generic-editor-wrap");
          if (curEditorWrap) curEditorWrap.style.height = `${parseInt(totalPageHeight)}px`;

          // increase or decrease active outline in block
          if (document.getElementById("active-page-selctor")) {
            document.getElementById("active-page-selctor").style.height = `${getZoomedValue(height, dimension.zoom)}px`;
          }

          // increase or decrease page margin in block into DOM
          if (document.querySelector(".dhp-page-margin-overlay")) {
            let marginvalue = parseInt(document.querySelector(".dhp-page-margin-overlay").style.top);
            document.querySelector(".dhp-page-margin-overlay").style.height = `${totalPageHeight - marginvalue * 2}px`;
          }
        } else {
          setTooltipValue(ActivePageHeight);
        }

        // Below code to reset block resizer tooltip position
        const blockTooltipContainer = document.querySelector(".block-resizer-tooltip .tooltip");

        if (blockTooltipContainer && refBottomResize.current) {
          const targetRect = refBottomResize.current.getBoundingClientRect();
          const tooltipRect = blockTooltipContainer.getBoundingClientRect();

          const targetTop = targetRect.top - 45;

          const deltaY = targetTop - tooltipRect.top;

          const transform = blockTooltipContainer.style.transform || "";
          const match = transform.match(
            /translate3d\(\s*(-?\d+(?:\.\d+)?)px,\s*(-?\d+(?:\.\d+)?)px,\s*(-?\d+(?:\.\d+)?)px\s*\)/
          );

          const currentX = targetRect.left + parseInt(targetRect.width / 2) - parseInt(tooltipRect.width / 2);
          const currentY = match ? parseFloat(match[2]) : 0;
          const currentZ = match ? parseFloat(match[3]) : 0;

          blockTooltipContainer.style.transform = `translate3d(
            ${currentX}px,
            ${currentY + deltaY}px,
            ${currentZ}px
          )`;
        }
      };

      const onMouseUpBottomResize = () => {
        setBlockHeight(height);
        setTooltipOpen(false);
        props.setIsResizing(false);
        if (!isResize) setIsResize(true);
        document.removeEventListener("mousemove", onMouseMoveBottomResize);
        document.removeEventListener("mouseup", onMouseUpBottomResize);
      };

      const onMouseDownResize = event => {
        setActivePage(blockContainer);
        event.preventDefault();
        y = event.clientY;
        const styles = window.getComputedStyle(resizableElem);
        height = parseInt(styles.height);
        document.addEventListener("mousemove", onMouseMoveBottomResize);
        document.addEventListener("mouseup", onMouseUpBottomResize);
      };

      const resizerBottom = refBottomResize.current;
      resizerBottom.addEventListener("mousedown", onMouseDownResize);

      return () => {
        resizerBottom.removeEventListener("mousedown", onMouseDownResize);
        document.removeEventListener("mouseup", onMouseUpBottomResize);
      };
    }
  }, [dimension]);

  return (
    <div
      ref={blockContainer}
      onMouseDown={e => {
        setActivePage(e); // set previous cloned obj ref false if active widget change
      }}
      onContextMenu={e => showRightContextMenu(e)}
      onDragOver={e => {
        (overrideDefault(e), fileHover());
      }}
      onDragEnter={e => {
        (overrideDefault(e), fileHover());
      }}
      onDragLeave={e => {
        (overrideDefault(e), fileHoverEnd());
      }}
      onDrop={e => {
        (overrideDefault(e), fileHoverEnd(), addFiles(e));
      }}
      className={cx(!props.pageSorterList ? [style["dhp-page-block"]] : "nav-block", {
        [style["custom-disable"]]: disableEditor,
      })}
      id={!props.pageSorterList ? props.blockNode.blockId : `dhp-nav-block-${props.blockNode.blockIdx}`}
      data-block-idx={props.blockNode.blockIdx}
      style={
        !props.pageSorterList
          ? {
              ...props.blockNode.style,
              ...safariBugfixCss,
              transform: `scale(${
                [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) ? 1 : dimension.zoom / 100
              })`,
            }
          : {
              ...props.blockNode.style,
              transform: `scale(${props.scaleFactor})`,
              transformOrigin: "0px 0px",
            }
      }>
      {backgroundColors.map(
        backgroundColor =>
          backgroundColor.blockId == props.blockNode.blockId && (
            <BlockBackgroundColor key={backgroundColor.blockId} backgroundColor={backgroundColor} />
          )
      )}

      {backgroundImages.map(
        backgroundImage =>
          backgroundImage.blockId == props.blockNode.blockId && (
            <BlockBackgroundImage key={backgroundImage.blockId} backgroundImage={backgroundImage} />
          )
      )}

      {widgets.map(
        widget =>
          widget.blockId == props.blockNode.blockId && (
            <Widget
              key={widget.id}
              widget={widget}
              pageSorterList={props.pageSorterList}
              scaleFactor={1}
              setOutsideDoubleclickedObj={props.setOutsideDoubleclickedObj}
              isSafari={isSafari}
              renderChart={props.renderChart}
            />
          )
      )}

      {[TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && (
        <div
          className={cx(
            style["block-resize-handler"],
            style["ui-resizable-s"],
            style["d-flex"],
            style["justify-content-center"],
            {
              [style["active"]]: tooltipOpen,
            }
          )}
          draggable={false}
          onDragStart={e => e.preventDefault()}
          ref={refBottomResize}>
          <div className={cx(style["d-flex"], style["icon-wraper"])} id={"Tooltip-" + props.blockNode.blockId}>
            <Icon icon="ui-grip-lines" additionalclass={style["ui-resizable-s"]} />
            <Tooltip
              placement="top"
              isOpen={tooltipOpen}
              target={"Tooltip-" + props.blockNode.blockId}
              className="block-resizer-tooltip"
              boundariesElement={document.getElementById("app")}>
              {tooltipValue}
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};
//Props type validation
Block.propTypes = {
  blockNode: PropTypes.object.isRequired,
  pageSorterList: PropTypes.bool,
  scaleFactor: PropTypes.number,
  setIsResizing: PropTypes.func,
  setOutsideDoubleclickedObj: PropTypes.func,
};

const PageMarginGrid = props => {
  const { documentType, dimension, pageMargin, grid } = useContext(EditorContext);

  let gridHeight = "100%",
    strokeWidth = grid.strokeWidth,
    gridSize = grid.size,
    zoomFactor = dimension.zoom / 100;

  if ([TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)) {
    gridHeight = `${parseFloat(dimension.height)}px`;
  } else {
    const isMacChrome =
      /Mac/.test(navigator.platform) &&
      /Chrome/.test(navigator.userAgent) &&
      !/Edge|OPR|Brave/.test(navigator.userAgent);

    strokeWidth = grid.strokeWidth * zoomFactor;
    strokeWidth = strokeWidth < 0.3 && isMacChrome ? 0.3 : strokeWidth; // in mac chrome under 0.3 it is not visible
    gridSize = grid.size * zoomFactor;
  }

  return (
    <>
      {grid.enabled && (
        <div
          className={cx(style["dhp-page-grid"], style[grid.color])}
          style={{ height: gridHeight, willChange: "transform" }}>
          <svg width="100%" height={gridHeight} xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id={`smallGrid-${props.unique}`}
                width={gridSize}
                height={gridSize}
                patternUnits="userSpaceOnUse">
                <path
                  d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`}
                  fill="none"
                  stroke="gray"
                  strokeWidth={strokeWidth}></path>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#smallGrid-${props.unique})`}></rect>
          </svg>
        </div>
      )}

      {pageMargin.enabled && <div className={style["dhp-page-margin-overlay"]} style={pageMargin.style}></div>}
    </>
  );
};
//Props type validation
PageMarginGrid.propTypes = {
  unique: PropTypes.string,
};

const Page = props => {
  let { blockNodes, metadata, pageSorterList, dimension, snapGrid, documentType, isTimeLineViewOpen } =
    useContext(EditorContext);
  const { pagePlayData } = useContext(EditorWrapperContext);
  let figureWidth = 144;
  let figureHeight = (figureWidth / dimension.width) * dimension.height;
  let zoomFactor = dimension.zoom / 100;

  const { start: initSortableList } = useSortableList();

  // Show controller on active page hover in pagelist section
  const pageListHover = e => {
    if (parseInt(e.target.closest(".pagelist-item").getAttribute("data-index")) === metadata.activePageIdx) {
      calculatePageListControllerTop(); // set controller top
      // add time out to stop transition when controller move to active page
      setTimeout(() => {
        document.querySelector(".page-sorter-controller").classList.add("show-page-list-controller");
      }, 300);
    } else {
      document.querySelector(".page-sorter-controller").classList.remove("show-page-list-controller");
    }
  };

  // set controller top
  const calculatePageListControllerTop = () => {
    let parentPos = document.getElementById("pageList").getBoundingClientRect(),
      childPos = document.getElementById(`fig-${metadata.activePageIdx}`).getBoundingClientRect();
    document.querySelector(".page-sorter-controller").style.top = `${childPos.top - parentPos.top + 4}px`;
  };

  return (
    <>
      {props.pageSorterList && (
        <li
          className={cx(
            { [style["active"]]: metadata.activePageIdx === parseInt(props.index) },
            style["pagelist-item"],
            style["sortable-item"]
          )}
          id={`fig-${props.index}`}
          data-index={props.index}
          data-canvas-id={props.pageNode.pageId}
          onMouseEnter={e => pageListHover(e)}
          onMouseDown={e => {
            (calculatePageListControllerTop(),
              initSortableList(
                {
                  id: `fig-${props.index}`,
                  idx: props.index,
                  pageId: props.pageNode.pageId,
                  blockId: blockNodes[props.index].blockId,
                },
                e
              ));
          }}>
          <div className={cx(style["content"])}>
            <figure
              className="m-0"
              style={{
                height: figureHeight,
                width: figureWidth,
              }}>
              {props.pageSorterList && props.visibleFigIndexes?.includes(`fig-${props.index}`) && (
                <div style={pageSorterList?.style} data-page-idx={props.pageNode.pageIdx}>
                  {blockNodes.map(
                    blockNode =>
                      blockNode.pageId == props.pageNode.pageId && (
                        <Block
                          key={blockNode.blockId}
                          blockNode={blockNode}
                          pageSorterList={props.pageSorterList}
                          scaleFactor={props.scaleFactor}
                          renderChart={props.renderChart}
                        />
                      )
                  )}
                </div>
              )}
              <div className={style["skeleton-loader-area"]}>
                <span className={style["loader-item"]} style={{ height: figureHeight, width: figureWidth }}></span>
              </div>
            </figure>
            <span>{parseInt(props.index) + 1}</span>
          </div>
          <div className={cx(style["placeholder"], style["d-none"])}>
            <figure
              style={{
                height: figureHeight,
                width: "144px",
              }}></figure>
          </div>
        </li>
      )}

      {!props.pageSorterList && (
        <>
          {/* canvas Grid Lines for snap to grid */}
          {[TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && metadata.activeWidgetId && (
            <>
              <SnapGrid component={"margin"} /> <SnapGrid component={"block-center"} />
            </>
          )}

          <div
            className={style["dhp-page-canvas"]}
            id={props.pageNode.pageId}
            data-page-idx={props.pageNode.pageIdx}
            style={{
              ...props.pageNode.style,
              width: `${
                [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
                  ? props.pageNode.style.width
                  : parseFloat(dimension.width) * zoomFactor
              }px`,
              height: `${
                [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
                  ? props.pageNode.style.height
                  : parseFloat(dimension.height) * zoomFactor
              }px`,
            }}>
            {![TYPE_INFOGRAPHIC, TYPE_PROPOSAL, TYPE_INTERACTIVE_DEMO].includes(documentType) &&
              !isTimeLineViewOpen && <PageTopOption pageIndex={props.index} toggle={props.toggle} />}

            <PageMarginGrid key={props.index} unique={props.index} />

            {((documentType?.toLowerCase() === TYPE_YOUTUBE_BANNER && dimension.size === "youtube-banner") ||
              (documentType?.toLowerCase() === TYPE_BROCHURE &&
                dimension.size === "a4" &&
                dimension.orientation === "landscape")) && <PageGuideLine />}

            {blockNodes.map(
              blockNode =>
                blockNode.pageId == props.pageNode.pageId && (
                  <React.Fragment key={blockNode.blockId}>
                    {/* canvas Grid Lines for snap to grid */}
                    {snapGrid?.blockGrid?.blockId === blockNode.blockId && (
                      <>
                        {![TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && (
                          <>
                            <SnapGrid component={"margin"} /> <SnapGrid component={"block-center"} />
                          </>
                        )}
                        <SnapGrid component={"block-middle"} />

                        {((documentType?.toLowerCase() === TYPE_YOUTUBE_BANNER &&
                          dimension.size === "youtube-banner") ||
                          (documentType?.toLowerCase() === TYPE_BROCHURE &&
                            dimension.size === "a4" &&
                            dimension.orientation === "landscape")) && <SnapGrid component={"guide-line"} />}
                      </>
                    )}

                    {![TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && (
                      <div style={{ height: "100%", width: "100%", overflow: "hidden" }}>
                        <Block
                          key={blockNode.blockId}
                          blockNode={blockNode}
                          setIsResizing={props.setIsResizing}
                          setOutsideDoubleclickedObj={props.setOutsideDoubleclickedObj}
                        />
                      </div>
                    )}
                    {[TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && (
                      <Block
                        key={blockNode.blockId}
                        blockNode={blockNode}
                        setIsResizing={props.setIsResizing}
                        setOutsideDoubleclickedObj={props.setOutsideDoubleclickedObj}
                        renderChart={props.renderChart}
                      />
                    )}
                  </React.Fragment>
                )
            )}

            {![TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
              !isTimeLineViewOpen &&
              pagePlayData.pageProgress > 0 &&
              pagePlayData.pageProgress < 100 &&
              pagePlayData.pageId === props.pageNode.pageId && <PageBottomOption />}
          </div>
        </>
      )}
    </>
  );
};

//Props type validation
Page.propTypes = {
  pageNode: PropTypes.object.isRequired,
  index: PropTypes.string.isRequired,
  pageSorterList: PropTypes.bool,
  scaleFactor: PropTypes.number,
  setIsResizing: PropTypes.func,
  setOutsideDoubleclickedObj: PropTypes.func,
  toggle: PropTypes.func,
};

const BlockSorterList = props => {
  let { metadata, pageSorterList, dimension, blockNodes } = useContext(EditorContext);
  let figureWidth = 144;
  let figureHeight = (figureWidth / dimension.width) * parseFloat(props.blockNode.style.height);

  const { start: initSortableList } = useBlockReorder();

  // Show controller on active page hover in pagelist section
  const pageListHover = e => {
    if (parseInt(e.target.closest(".pagelist-item").getAttribute("data-index")) === metadata.activeBlockIdx) {
      calculatePageListControllerTop(); // set controller top
      // add time out to stop transition when controller move to active page
      setTimeout(() => {
        document.querySelector(".page-sorter-controller").classList.add("show-page-list-controller");
      }, 300);
    } else {
      document.querySelector(".page-sorter-controller").classList.remove("show-page-list-controller");
    }
  };

  // set controller top
  const calculatePageListControllerTop = () => {
    let parentPos = document.getElementById("pageList").getBoundingClientRect(),
      childPos = document.getElementById(`fig-${metadata.activeBlockIdx}`).getBoundingClientRect();
    document.querySelector(".page-sorter-controller").style.top = `${childPos.top - parentPos.top + 4}px`;
  };

  useEffect(() => {
    calculatePageListControllerTop();
  }, [metadata.activeBlockIdx]);

  return (
    <li
      className={cx(
        { [style["active"]]: metadata.activeBlockIdx === parseInt(props.index) },
        style["pagelist-item"],
        style["sortable-item"]
      )}
      id={`fig-${props.index}`}
      data-index={props.index}
      data-canvas-id={props.blockNode.blockId}
      onMouseEnter={e => pageListHover(e)}
      onMouseDown={e => {
        initSortableList(
          {
            id: `fig-${props.index}`,
            idx: props.index,
            blockId: blockNodes[props.index].blockId,
          },
          e
        );
      }}>
      <div className={cx(style["content"])}>
        <figure
          className="m-0"
          style={{
            height: figureHeight,
            width: figureWidth,
          }}>
          {props.pageSorterList && props.visibleFigIndexes?.includes(`fig-${props.index}`) && (
            <div style={pageSorterList?.style} data-block-idx={props.blockNode.blockIdx}>
              <Block
                key={props.blockNode.blockId}
                blockNode={props.blockNode}
                pageSorterList={props.pageSorterList}
                scaleFactor={props.scaleFactor}
                renderChart={props.renderChart}
              />
            </div>
          )}
          <div className={style["skeleton-loader-area"]}>
            <span className={style["loader-item"]} style={{ height: figureHeight, width: figureWidth }}></span>
          </div>
        </figure>
        <span>{parseInt(props.index) + 1}</span>
      </div>
      <div className={cx(style["placeholder"], style["d-none"])}>
        <figure
          style={{
            height: figureHeight,
            width: "144px",
          }}></figure>
      </div>
    </li>
  );
};
//Props type validation
BlockSorterList.propTypes = {
  index: PropTypes.string.isRequired,
  pageSorterList: PropTypes.bool,
  scaleFactor: PropTypes.number,
};

const EditorCanvas = ({ pageSorterList, scaleFactor, ...props }) => {
  const { id: documentId } = useParams();
  const dispatch = useDispatch();

  const {
    pageNodes,
    blockNodes,
    metadata,
    dimension,
    isDocumentReady,
    rightContextMenu,
    updateRightContextMenu,
    isTimeLineViewOpen,
    audios,
    widgets,
    updateWidgets,
    setIsDocumentReady,
    HistoryList,
    isDocumentSigned,
    documentType,
  } = useContext(EditorContext);
  const { playingPageIdx, setWhichPagePlaying, ignoreNextScrollEvent, changeBySocket, showFindAndReplace } =
    useContext(EditorWrapperContext);

  let {
    updateDoubleClickActive,
    doubleClickActive,
    setSelectionRange,
    textSelectedAreaColor,
    setTextSelectedAreaColor,
    setDoubleClickSelctedareaColor,
    setPreviousSelection,
  } = useContext(EditorPanelContext);
  const { setPasteColorActive } = useContext(AppContext);

  const { versionRestored: isVersionRestored } = useSelector(state => state.document);

  const [isResizing, setIsResizing] = useState(false);
  const [outsideDoubleclickedObj, setOutsideDoubleclickedObj] = useState();
  const { selectedPartFontFormatting } = useFontFormatting();
  const { handleFocusOut } = useTextFocusOut();
  const [visibleFigIndexes, setVisibleFigIndexes] = useState();

  // Active block on page scroll
  useHandelKeyBoardEvent();
  useCopyWidget();
  useCloneWidget();
  usePasteWidget();
  useCutWidget();
  useBlockActiveOnscroll(metadata.activeBlockId, ignoreNextScrollEvent, isResizing);
  useEditorDocumentProcessing(pageSorterList); // Editor document process (e.g., Saving changes, screenshot generate, version generate)
  const { start: initDocumentReader } = useDocumentReader();
  useMergeFields();
  usePricingTableEvent();
  useScopeOfWorkEvent();
  useItineraryEvent();

  // Update Text widget Handler height when double click and edit text widget
  const updateWidgetHandlerHeight = e => {
    let id = metadata.activeWidgetId[0];
    let isGroupWidget = document.getElementById(id)?.closest(".dhp-page-group");
    let targetId = isGroupWidget ? document.getElementById(id).closest(".dhp-root-widget").getAttribute("id") : id;

    if ([TEXT, BUTTON].includes(metadata.activeWidgetType[0])) {
      let curSelection = document.querySelector(`[data-widget-id="${targetId}"].collborative-selector`);
      if (isGroupWidget) {
        let { groupDivHeight } = getUpdatedGroupInnerWidgetPos(targetId, id, e.target.offsetHeight, dimension.zoom);

        // update DOM and group handler
        document.querySelector(`.dhp-content-editable-true-text .child-selected`).style.outline = "0px solid #00a4ef";
        document.querySelector(`.dhp-content-editable-true-text .child-selected .dhp-widget-inner`).style.outline =
          "2px solid #00a4ef";
        document.getElementById("dhp-widget-handler").style.height = `${groupDivHeight}px`;
        if (curSelection) curSelection.style.height = `${groupDivHeight}px`;
      } else {
        let containEditor = document.querySelector(".dhp-content-editable-true-text");
        // handle rotated text widget
        let handler = document.getElementById("dhp-widget-handler");
        const {
          translate: { x: handlerTransX, y: handlerTransY },
          rotate: { theta: handlerTheta },
        } = getCssTransformObj({
          transform: handler.style.transform,
        });
        const { left, top } = calculateNewPositionOnRotatedObjectResize(
          parseFloat(handlerTransX),
          parseFloat(handlerTransY),
          parseFloat(handler.style.width),
          parseFloat(e.target.offsetHeight),
          parseFloat(handler.style.width),
          parseFloat(handler.style.height),
          parseFloat(handlerTheta)
        );
        const handlerTransformStr = getCssTransformObj({
          translateX: `${left}px`,
          translateY: `${top}px`,
          transform: handler.style.transform,
          returnStr: true,
        });

        handler.style.transform = handlerTransformStr;
        handler.style.height = `${e.target.offsetHeight}px`;
        containEditor.style.transform = handlerTransformStr;
        containEditor.style.height = `${e.target.offsetHeight}px`;

        if (curSelection) curSelection.style.height = `${e.target.offsetHeight}px`;
      }
    }
  };

  // hold the previous selction range for safari
  const holdSelectionRange = () => {
    setPreviousSelection({
      start: window.getSelection().getRangeAt(0).startOffset,
      end: window.getSelection().getRangeAt(0).endOffset,
      startContainer: window.getSelection().getRangeAt(0).startContainer,
      endContainer: window.getSelection().getRangeAt(0).endContainer,
    });
  };

  const clickOutsideContentEditebleText = e => {
    let isClickOutside;
    let flyoutEl = document.querySelector(".dhp-content-editable-true-text"),
      targetEl = e.target.closest(".dhp-content-editable-true-text"); // clicked element

    if (targetEl == flyoutEl) {
      // This is a click inside, does nothing, just return.
      isClickOutside = false;
    } else {
      // This is a click outside.
      isClickOutside = true;
    }

    if (!isClickOutside) return;
    else if (metadata.activeWidgetType[0] === BUTTON) {
      handleFocusOut(updateDoubleClickActive);
      setTextSelectedAreaColor(false);
      toggleZoomStyle({ type: "inject" });
    } else {
      if (e.target.closest("#bold-wrap")) {
        setSelectionRange(false);
        selectedPartFontFormatting("bold");
        setTimeout(() => {
          setSelectionRange("bold");
        }, 10);
      } else if (e.target.closest("#italic-wrap")) {
        setSelectionRange(false);
        selectedPartFontFormatting("italic");
        setTimeout(() => {
          setSelectionRange("italic");
        }, 10);
      } else if (e.target.closest("#underline-wrap")) {
        setSelectionRange(false);
        selectedPartFontFormatting("underline");
        setTimeout(() => {
          setSelectionRange("underline");
        }, 10);
      } else if (
        e.target.closest(".color-wrap") &&
        !document.getElementById(metadata.activeWidgetId[0]).getAttribute("data-grad-scolor")
      ) {
        // focus on input box of color section if click on input box
        if (e.target?.id === "Color-Input") {
          window.getSelection().removeAllRanges();
          e.target.focus();
          setPasteColorActive(false);
        }
      } else if (
        (e.target.closest(".dhp-navAside") || e.target.closest(".dhp-secondAside")) &&
        document.querySelector(".merge-field-asset-pane")
      ) {
        // PREVENT TEXT FOUCUS OUT if clicked on {{ mf-selector-modal OR mf-info-modal }} from left asset pane DURING MF OPERTAIONS
      } else {
        handleFocusOut(updateDoubleClickActive);
        setTextSelectedAreaColor(false);
        toggleZoomStyle({ type: "inject" });
      }
    }
  };

  const allEqual = arr => arr.every(val => val === arr[0]);

  //fetch color on double for firefox browser
  const fetchColorForFireFox = () => {
    let userAgent = navigator.userAgent;
    let isFirefox = userAgent.match(/firefox|fxios/i);

    if (!isFirefox) return;

    setDoubleClickSelctedareaColor();
    let sel, activeHtml, selectedPart, parentEl;
    let colorArray = [];

    sel = window.getSelection();

    // get parent elemet of selected part
    if (window.getSelection) {
      if (sel.rangeCount) {
        parentEl = sel.getRangeAt(0).commonAncestorContainer;
        if (parentEl.nodeType != 1) {
          parentEl = parentEl.parentNode;
        }
      }
    }

    // get html of selected part
    if (sel.rangeCount) {
      let container = document.createElement("div");
      for (let i = 0, len = sel.rangeCount; i < len; ++i) {
        container.appendChild(sel.getRangeAt(i).cloneContents());
      }
      activeHtml = container.innerHTML;
      selectedPart = document.createElement("span");
      selectedPart.innerHTML = activeHtml;
    }

    let parentColor = parentEl?.style?.color
      ? parentEl.style?.color
      : document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.color;
    let selColor = parentColor;

    selectedPart?.querySelectorAll("*").forEach(node => {
      if (node.style?.color) colorArray.push(node.style.color);
    });

    if (colorArray.length === 0) {
      selectedPart?.querySelectorAll("*").forEach(node => {
        if (node.style?.color && parentColor !== node.style?.color) {
          selColor = "Mixed Colors";
        }
      });
    } else if (allEqual(colorArray)) {
      selColor = colorArray[0];
    } else {
      selColor = "Mixed Colors";
    }

    setDoubleClickSelctedareaColor(selColor);
  };

  const handleScroll = e => {
    if (e.target.closest("#merge-field-selector-modal") || e.target.closest("#merge-field-info-modal")) {
      // prevent text focus out if event target is from merge field selector modal or merge field info modal
      // (keyboard typing cmf value via text field was triggering scroll event, resulting text focus out)
      return;
    }

    // Focusout triggerd if any text or text frame widget is contenteditable
    if (document.querySelector(".dhp-content-editable-true-text")) handleFocusOut(updateDoubleClickActive);

    // remove right context menu if it is open
    if (rightContextMenu.enable) {
      if (e.target.closest(".link-modal") === null && e.target.closest(".widget-role-modal") === null) {
        updateRightContextMenu({
          ...rightContextMenu,
          enable: false,
        });
      }
    }
  };

  // get the fighure indexes which is in viewport
  const getFigIndexInViewPortOnPageSorter = () => {
    let visibleIndexArray = [];
    document.querySelectorAll(".pagelist-item .content figure").forEach((element, idx) => {
      // let isFigVisable = isElementVisible(element);
      // if (isFigVisable) visibleIndexArray.push(element.closest(".pagelist-item").getAttribute("id"));
      visibleIndexArray.push(element.closest(".pagelist-item").getAttribute("id"));
    });
    setVisibleFigIndexes(visibleIndexArray);
  };

  // check when scroll timeline view which figure portion is visible
  const isElementVisible = element => {
    const viewportTop = document.getElementById("pageList").getBoundingClientRect().top;
    const viewportBottom = viewportTop + document.getElementById("pageList").getBoundingClientRect().height;
    const elementTop = element.getBoundingClientRect().top;
    const elementBottom = elementTop + element.getBoundingClientRect().height;

    return elementBottom > viewportTop && elementTop < viewportBottom;
  };

  useEffect(() => {
    setTimeout(() => {
      getFigIndexInViewPortOnPageSorter();
    }, 100);
  }, [pageSorterList, blockNodes]);

  // trigger whenever text selected part color is changed
  useEffect(() => {
    if (textSelectedAreaColor) {
      selectedPartFontFormatting("color");
    }
  }, [textSelectedAreaColor]);

  // trigger when doubleclick is activated
  useLayoutEffect(() => {
    if (doubleClickActive) {
      if (![TEXT, TEXT_FRAME, BUTTON].includes(outsideDoubleclickedObj.dataAssetType)) {
        updateDoubleClickActive(false);
        return;
      }

      let outsideElem = document.querySelector(".dhp-content-editable-true-text");

      // hide Main text/text-frame/Group widget
      let id = outsideDoubleclickedObj.dataWidgetId;
      let isGroupWidget = document.getElementById(id).closest(".dhp-page-group");
      let targetId = isGroupWidget ? document.getElementById(id).closest(".dhp-root-widget").getAttribute("id") : id;
      document.getElementById(targetId).style.visibility = "hidden";

      // add style on outside text/text-frame/Group widget main div on double click actiavte
      outsideElem.style.cssText = getCssTextObjToString(outsideDoubleclickedObj?.style);
      outsideElem.setAttribute("data-widget-id", outsideDoubleclickedObj.dataWidgetId);
      outsideElem.setAttribute("data-asset-type", outsideDoubleclickedObj.dataAssetType);
      outsideElem.innerHTML = outsideDoubleclickedObj.innerHTML;

      // adjust style on outside text widget inner div on double click actiavte
      if ([TEXT, BUTTON].includes(metadata.activeWidgetType[0])) {
        // For group text update
        if (isGroupWidget) {
          //Adjust width and font size for all text widgets in a group and focus child selected widget only
          outsideElem.querySelectorAll(".dhp-page-widget").forEach(element => {
            element.classList.remove("dhp-widget-hyperlinked"); // remove hyperlink class if widget has hyperlinked

            let targetElem = element.querySelector(".dhp-widget-inner");
            let targetElemIsChildSelcted = element.classList.contains("child-selected");
            let strokeValue = targetElem.style.webkitTextStroke;

            targetElem.style.width = `${getZoomedValue(targetElem.style.width, dimension.zoom)}px`;
            targetElem.style.fontSize = `${getZoomedValue(targetElem.style.fontSize, dimension.zoom)}px`;
            targetElem.style.letterSpacing = `${getZoomedValue(targetElem.style.letterSpacing, dimension.zoom)}px`;

            if (targetElem.style.borderWidth) {
              targetElem.style.borderWidth = `${getZoomedValue(targetElem.style.borderWidth, dimension.zoom)}px`;
              targetElem.style.borderRadius = `${getZoomedValue(targetElem.style.borderRadius, dimension.zoom)}px`;
            }
            if (strokeValue) {
              let strokeArray = strokeValue.split(" ");
              let rgbValue =
                strokeArray[1] === "transparent"
                  ? strokeArray[1]
                  : RGBToHex(`${strokeArray[1]} ${strokeArray[2]} ${strokeArray[3]}`);
              let zoomedStrokeValue = getZoomedValue(parseInt(strokeArray[0]), dimension.zoom);

              targetElem.style.webkitTextStroke = `${zoomedStrokeValue}px ${rgbValue}`;
            }

            targetElem.style.outline = "none";

            if (targetElemIsChildSelcted) {
              element.classList.add("focusout-child-selected");
              targetElem.style["-webkit-user-select"] = "text";
              targetElem.style["user-select"] = "text";
              targetElem.setAttribute("contenteditable", true);
              targetElem.focus();
            }
          });
        }
        // For single text update
        else {
          let targetElem = outsideElem.querySelector(".dhp-widget-inner");
          let filterValue = targetElem.style.filter;
          let strokeValue = targetElem.style.webkitTextStroke;

          targetElem.style.width = `${getZoomedValue(targetElem.style.width, dimension.zoom)}px`;
          targetElem.style.fontSize = `${getZoomedValue(targetElem.style.fontSize, dimension.zoom)}px`;
          targetElem.style.letterSpacing = `${getZoomedValue(targetElem.style.letterSpacing, dimension.zoom)}px`;

          if (targetElem.style.borderWidth) {
            targetElem.style.borderWidth = `${getZoomedValue(targetElem.style.borderWidth, dimension.zoom)}px`;
            targetElem.style.borderRadius = `${getZoomedValue(targetElem.style.borderRadius, dimension.zoom)}px`;
          }
          if (filterValue && filterValue !== "none") {
            let filterArray = filterValue.split("drop-shadow")[1].split(" ");
            let rgbValue = filterArray[0].split("(");
            rgbValue =
              rgbValue[1] === "transparent"
                ? rgbValue[1]
                : RGBToHex(`${rgbValue[1]}(${rgbValue[2]} ${filterArray[1]} ${filterArray[2]}`);

            let horaizentalShadow = `${getZoomedValue(
              parseInt(filterArray[rgbValue === "transparent" ? 1 : 3]),
              dimension.zoom
            )}px`;
            let verticalShadow = `${getZoomedValue(
              parseInt(filterArray[rgbValue === "transparent" ? 2 : 4]),
              dimension.zoom
            )}px`;
            let blur = `${getZoomedValue(parseInt(filterArray[rgbValue === "transparent" ? 3 : 5]), dimension.zoom)}px`;
            let shadowColor = rgbValue;
            targetElem.style.filter = `drop-shadow(${horaizentalShadow} ${verticalShadow} ${blur} ${shadowColor})`;
          }
          if (strokeValue) {
            let strokeArray = strokeValue.split(" ");
            let rgbValue =
              strokeArray[1] === "transparent"
                ? strokeArray[1]
                : RGBToHex(`${strokeArray[1]} ${strokeArray[2]} ${strokeArray[3]}`);
            let zoomedStrokeValue = getZoomedValue(parseInt(strokeArray[0]), dimension.zoom);

            targetElem.style.webkitTextStroke = `${zoomedStrokeValue}px ${rgbValue}`;
          }

          targetElem.style.outline = "none";
          targetElem.style["-webkit-user-select"] = "text";
          targetElem.style["user-select"] = "text";
          targetElem.setAttribute("contenteditable", true);
          targetElem.focus();

          //for safari shifiting issue
          var styleElem = document.head.appendChild(document.createElement("style"));
          styleElem.setAttribute("id", "contenteditableStyle");
          styleElem.innerHTML = "*, :after, :before {box-sizing: border-box;}";
        }
      }

      // adjust style on outside text-frame widget inner div on double click actiavte
      if (
        metadata.activeWidgetType[0] === TEXT_FRAME &&
        parseInt(document.getElementById(metadata.activeWidgetId[0]).getAttribute("data-version")) < 3
      ) {
        let targetElem = isGroupWidget
          ? outsideElem.querySelector(".child-selected .dhp-widget-inner .active-text")
          : outsideElem.querySelector(".dhp-widget-inner .active-text");
        let svgElem = isGroupWidget
          ? outsideElem.querySelector(".child-selected .dhp-widget-inner")
          : outsideElem.querySelector(".dhp-widget-inner");
        let filterValue = svgElem.style.filter;

        if (isGroupWidget) {
          outsideElem.querySelectorAll(".dhp-page-widget").forEach(element => {
            element.classList.remove("dhp-widget-hyperlinked"); // remove hyperlink class if widget has hyperlinked
          });
        }

        outsideElem.querySelector(".child-selected")?.classList.add("focusout-child-selected");

        if (filterValue && filterValue !== "none") {
          let filterArray = filterValue.split("drop-shadow")[1].split(" ");
          let rgbValue = filterArray[0].split("(");
          rgbValue =
            rgbValue[1] === "transparent"
              ? rgbValue[1]
              : RGBToHex(`${rgbValue[1]}(${rgbValue[2]} ${filterArray[1]} ${filterArray[2]}`);

          let horaizentalShadow = `${getZoomedValue(
            parseInt(filterArray[rgbValue === "transparent" ? 1 : 3]),
            dimension.zoom
          )}px`;
          let verticalShadow = `${getZoomedValue(
            parseInt(filterArray[rgbValue === "transparent" ? 2 : 4]),
            dimension.zoom
          )}px`;
          let blur = `${getZoomedValue(parseInt(filterArray[rgbValue === "transparent" ? 3 : 5]), dimension.zoom)}px`;
          let shadowColor = rgbValue;
          svgElem.style.filter = `drop-shadow(${horaizentalShadow} ${verticalShadow} ${blur} ${shadowColor})`;
        }

        targetElem.style.outline = "none";
        targetElem.style["-webkit-user-select"] = "text";
        targetElem.style["user-select"] = "text";
        targetElem.setAttribute("contenteditable", true);
        targetElem.focus();
      }
    }
  }, [doubleClickActive]);

  // hide all pages accept active page : when timeLine is open
  useEffect(() => {
    if (isTimeLineViewOpen) {
      setTimeout(() => {
        document.querySelectorAll(".dhp-page-canvas").forEach(element => {
          if (element.getAttribute("id") !== metadata.activePageId) {
            element.style.display = "none";
          } else element.style.display = "block";
        });
      }, 100);
    } else {
      document.querySelectorAll(".dhp-page-canvas").forEach(element => {
        element.style.display = "block";
      });
    }
  }, [metadata.activePageId, isTimeLineViewOpen, changeBySocket]);

  useEffect(() => {
    if (!isTimeLineViewOpen) {
      let OutsideWidgetCanvasArray = [];

      // fetch outside widget after widget or block change
      document.querySelectorAll(`#${metadata.activeBlockId} .dhp-root-widget`).forEach(element => {
        let isWidgetsOutsideCanvas = checkChildOutsideParent({
          parent: `#${metadata.activeBlockId}`,
          child: element,
        });
        if (
          isWidgetsOutsideCanvas &&
          element.style.display !== "none" &&
          (!metadata.activeWidgetId ||
            (metadata.activeWidgetId && !metadata.activeWidgetId.includes(element.getAttribute("id"))))
        )
          OutsideWidgetCanvasArray.push(element.getAttribute("id"));
      });

      // delete outside widget after activewidget got focused out (multi widget)
      if (
        OutsideWidgetCanvasArray.length > 0 &&
        (!metadata.activeWidgetId || metadata.activeWidgetId.length === 0 || metadata.activeWidgetId.length === 1)
      ) {
        let newWidget = widgets.filter(widget => !OutsideWidgetCanvasArray.includes(widget.id));
        updateWidgets(newWidget);
      }
    }
  }, [metadata.activeWidgetId, metadata.activeBlockId]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, true);

    // trigger on mousedown when doubleclick is activate for text
    if (doubleClickActive) {
      document.addEventListener("mousedown", clickOutsideContentEditebleText);
    }
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      setTimeout(() => {
        document.removeEventListener("mousedown", clickOutsideContentEditebleText);
      }, 10);
    };
  });

  // document play, audio play, page play, functionalities
  useDocumentPlayV2();
  // useDocumentPlay();
  useAudioPlay();
  usePagePlay();

  const handleClickOutsideForTimelinePlay = e => {
    const playingPageIdx = document.querySelector(".dhp-page-canvas.page-playing")?.getAttribute("data-page-idx");
    if (
      playingPageIdx &&
      !e?.target?.closest(`#page-top-play-icon-${playingPageIdx}`) &&
      !e?.target?.closest(".dhp-page-progress-bar") &&
      !e?.target?.classList.contains("dhp-page-progress-bar") &&
      !e?.target?.closest(".page-timeline-player") &&
      !e?.target?.closest(".page-timeline-playhead") &&
      !e?.target?.classList.contains("timeline-body") &&
      !e?.target?.closest(".document-timeline-player") &&
      !e?.target?.closest(".document-timeline-playhead")
    ) {
      setWhichPagePlaying({
        action: "force_stop",
        playingPageIdx: parseInt(playingPageIdx),
      });
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutsideForTimelinePlay);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideForTimelinePlay);
    };
  }, []);

  // Trigger document Reader hook after veriosn restore.
  useEffect(() => {
    if (isVersionRestored) {
      dispatch(fetchDocumentDetails(documentId)).then(resp => {
        if (resp?.result?.data) {
          setIsDocumentReady(false);
          HistoryList.length = 1; // Reset history list to disable undo redo
          initDocumentReader({ data: resp.result.data });
        }
      });
    }
  }, [isVersionRestored]);

  useEffect(() => {
    // lock editor if proposal is signed
    if (isDocumentSigned) {
      setTimeout(() => {
        document.getElementById("generic-editor-wrap").style.pointerEvents = "none";
      }, 100);
    } else {
      if (document.getElementById("generic-editor-wrap"))
        document.getElementById("generic-editor-wrap").style.pointerEvents = "";
    }
  }, [isDocumentSigned]);

  // trigger selectable on active widget style change: START
  const { start: initSelectable } = useSelectable();
  let isGroupWidget = document.getElementById(metadata.activeWidgetId[0])?.closest(".dhp-page-group.dhp-root-widget");
  let targetId = isGroupWidget?.id ?? metadata.activeWidgetId[0];
  const ACTIVE_WIDGET_STYLE = widgets?.find(w => w.id === targetId)?.style;

  useEffect(() => {
    if (isDocumentReady) initSelectable({ finalUpdate: true });
  }, [ACTIVE_WIDGET_STYLE]);
  // trigger selectable on active widget style change: END

  return (
    <>
      {!pageSorterList && isDocumentReady && (
        <>
          <div id="generic-editor-wrap" className={style["generic-editor-wrap"]}>
            {isDocumentSigned && <div className="signed-editor-overlay"></div>}

            {pageNodes.map((pageNode, idx) => (
              <Page
                key={pageNode.pageId}
                pageNode={pageNode}
                index={idx + getRandomString()}
                setIsResizing={setIsResizing}
                setOutsideDoubleclickedObj={setOutsideDoubleclickedObj}
                toggle={props.toggle}
                visibleFigIndexes={visibleFigIndexes}
              />
            ))}
          </div>

          <CollaborativeSelector />

          <WidgetHandler />

          {/* Display when hover on a widget */}
          <div id="dhp-widget-highlighter" className={cx(style["dhp-widget-highlighter"], style["d-none"])}>
            <div className={cx(style["widget-role-indicator"], style["d-none"])}>
              <Icon icon="ui-tick-success" additionalclass="color-success" />
            </div>
          </div>

          {/* active page selector from outside */}
          {metadata.activeBlockId &&
            !metadata.activeWidgetId &&
            !metadata.activeOutSideCanvasArea &&
            playingPageIdx === null && <ActivePageSelector />}

          {/* outside text/text-frame widget on doubleclick */}
          {doubleClickActive && (
            <div
              className={getClassListToString(outsideDoubleclickedObj.classLists)}
              style={{ visibility: "hidden" }}
              onMouseUp={() => {
                (setSelectionRange(true), holdSelectionRange());
              }}
              onKeyUp={() => {
                (setSelectionRange(true), holdSelectionRange());
              }}
              onDoubleClick={fetchColorForFireFox}
              onMouseDown={() => {
                (setSelectionRange(false), setDoubleClickSelctedareaColor(false));
              }}
              onKeyDown={() => {
                (setSelectionRange(false), setDoubleClickSelctedareaColor(false));
              }}
              onInput={e => {
                updateWidgetHandlerHeight(e);
                toggleZoomStyle({ type: "revoke" });
              }}
              onFocus={() => toggleZoomStyle({ type: "revoke" })}></div>
          )}

          {/* Display when finding string from text and table */}
          {showFindAndReplace?.isActive && (
            <div id="dhp-find-highlighter" className={cx(style["dhp-find-highlighter"], style["d-none"])}></div>
          )}

          {/* Audio elements */}
          <div id="dhp-audio-files">
            {audios?.map((audio, idx) => (
              <audio
                id={`player-${audio.id}`}
                key={idx}
                src={audio.src}
                preload="auto"
                data-audio-id={audio.id}></audio>
            ))}
          </div>
        </>
      )}

      {pageSorterList && isDocumentReady && (
        <>
          {![TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
            pageNodes.map((pageNode, idx) => (
              <Page
                key={pageNode.pageId}
                pageNode={pageNode}
                index={idx + getRandomString()}
                pageSorterList={pageSorterList}
                scaleFactor={scaleFactor}
                visibleFigIndexes={visibleFigIndexes}
                renderChart={props.renderChart}
              />
            ))}

          {[TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
            blockNodes.map((blockNode, idx) => (
              <BlockSorterList
                key={blockNode.blockId}
                blockNode={blockNode}
                index={idx + getRandomString()}
                pageSorterList={pageSorterList}
                scaleFactor={scaleFactor}
                visibleFigIndexes={visibleFigIndexes}
                renderChart={props.renderChart}
              />
            ))}
        </>
      )}
    </>
  );
};
//Props type validation
EditorCanvas.propTypes = {
  pageSorterList: PropTypes.bool,
  scaleFactor: PropTypes.number,
  toggle: PropTypes.func,
};

export default EditorCanvas;
